import { useQuery, useMutation } from 'react-apollo';
import { useParams } from 'react-router-dom';

import { setAccessAuthToken } from '../../services/password-store/passwordStore';
import { getErrorString } from '../../../../util/normalize-errors';
import { hierarchyAuthTokenCreate as hierarchyAuthTokenCreateMutation } from '../../graphql/mutations/hierarchy-token';
import { HierarchyItemForPasswordValidation } from '../../graphql/queries/hierarchy-item';

/**
 * Hook responsible for Web Gallery password authorization.
 *
 * @param {Object} options The options.
 * @param {string} options.password The password as supplied by the user.
 * @param {Function} [options.onVerified] Callback - called on successful authorization.
 * @returns {{onFormSubmit: onFormSubmit, submitting: boolean, loading: boolean, error: (string|any), hierarchyItem}}
 */
const useWebGalleryPasswordEntryState = ({ password, onVerified }) => {
  const { recordId } = useParams();

  // Verifying once for an Album or AlbumFolder gives access to the entire sub-tree
  // of that root AlbumFolder, so we store and check the password access permissions on the
  // highest level possible i.e. root AlbumFolder ID for everything except
  // individual Albums where we check the Album ID instead.

  // Fetch the root of the "record" that lead to this screen:
  const {
    loading: loadingHierarchyItem,
    error: hierarchyItemError,
    data: { hierarchyItem } = {},
  } = useQuery(HierarchyItemForPasswordValidation, {
    variables: {
      id: recordId,
    },
    skip: !recordId,
  });

  // The root is either the root AlbumFolder (of either an AlbumFolder or an Album)
  // or the Album itself (for individual albums).
  const rootRecordId =
    hierarchyItem?.album_folder?.root.id ??
    hierarchyItem?.album.root_album_folder?.id ??
    hierarchyItem?.album.id;

  // Create the HierarchyAuthToken and on success add it in local storage.
  const [
    hierarchyAuthTokenCreate,
    {
      loading: loadingHierarchyAuthTokenCreate,
      error: hierarchyAuthTokenCreateError,
    },
  ] = useMutation(hierarchyAuthTokenCreateMutation, {
    errorPolicy: 'all',
    onCompleted: response => {
      const token = response.hierarchyAuthTokenCreate.auth_token;

      setAccessAuthToken(rootRecordId, token);
      onVerified?.({ rootRecordId, recordId, token });
    },
  });

  // Password form submission. Kicks in `hierarchyAuthTokenCreate`.
  const onFormSubmit = async event => {
    event.preventDefault();

    try {
      await hierarchyAuthTokenCreate({
        variables: {
          input: {
            id: rootRecordId,
            password,
          },
        },
      });
    } catch {
      // Nothing to do here, the error is tracked as a response of the mutation.
    }
  };

  return {
    submitting: loadingHierarchyAuthTokenCreate,
    loading: loadingHierarchyItem,
    error: getErrorString(hierarchyAuthTokenCreateError || hierarchyItemError),
    hierarchyItem,
    onFormSubmit,
  };
};

export default useWebGalleryPasswordEntryState;
